import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, Search, FileText, Loader2, AlertCircle, X, Filter } from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';
import { ProductAutocomplete } from './forms/ProductAutocomplete';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const ProformaInvoices = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [fCustomerId, setFCustomerId] = useState('');
  const [fCompany, setFCompany] = useState('');
  const [fCompanyInput, setFCompanyInput] = useState('');
  const [showCompanySuggest, setShowCompanySuggest] = useState(false);
  const [fStatus, setFStatus] = useState('');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fActivePreset, setFActivePreset] = useState('');
  const [applied, setApplied] = useState({ customerId: '', company: '', status: '', dateFrom: '', dateTo: '' });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try { setLoading(true);
      const [res, prodsRes, cRes] = await Promise.all([axios.get(`${BACKEND_URL}/api/proforma-invoices`, getAuthHeaders()), axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()), axios.get(`${BACKEND_URL}/api/customers`, getAuthHeaders())]);
      setItems(res.data); setCustomers(cRes.data || []);
      const map = {}; (prodsRes.data || []).forEach(p => { map[p.id] = p.name; }); setProductMap(map);
    } catch (err) { setError('Failed to fetch proforma invoices'); } finally { setLoading(false); }
  };
  const companyNames = [...new Set(items.map(i => i.company_name).filter(Boolean))].sort();
  const companySuggestions = fCompanyInput.trim() ? companyNames.filter(n => n.toLowerCase().includes(fCompanyInput.toLowerCase())) : companyNames.slice(0, 8);

  function calcPreset(preset) {
    const now = new Date(); let fd = '', td = '';
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'this_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth(), 1)); td = fmt(now); }
    else if (preset === 'last_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth()-1, 1)); td = fmt(new Date(now.getFullYear(), now.getMonth(), 0)); }
    else if (preset === 'this_quarter') { const qs = Math.floor(now.getMonth()/3)*3; fd = fmt(new Date(now.getFullYear(), qs, 1)); td = fmt(now); }
    else if (preset === 'last_quarter') { const qs = Math.floor(now.getMonth()/3)*3-3; const y = qs<0?now.getFullYear()-1:now.getFullYear(); const qss=((qs%12)+12)%12; fd=fmt(new Date(y,qss,1)); td=fmt(new Date(y,qss+3,0)); }
    else if (preset === 'this_year') { fd = fmt(new Date(now.getFullYear(), 0, 1)); td = fmt(now); }
    else if (preset === 'last_year') { fd = fmt(new Date(now.getFullYear()-1, 0, 1)); td = fmt(new Date(now.getFullYear()-1, 11, 31)); }
    return { fd, td };
  }
  const applyPreset = (preset) => { const { fd, td } = calcPreset(preset); setFActivePreset(preset); setFDateFrom(fd); setFDateTo(td); };
  const applyFilters = () => setApplied({ customerId: fCustomerId, company: fCompany, status: fStatus, dateFrom: fDateFrom, dateTo: fDateTo });
  const clearFilters = () => { setFCustomerId(''); setFCompany(''); setFCompanyInput(''); setFStatus(''); setFDateFrom(''); setFDateTo(''); setFActivePreset(''); setApplied({ customerId: '', company: '', status: '', dateFrom: '', dateTo: '' }); };
  const hasFilters = applied.customerId || applied.company || applied.status || applied.dateFrom || applied.dateTo;

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/proforma-invoices/${id}`, getAuthHeaders());
      setDeleteConfirm(null); fetchItems();
    } catch (err) { setError('Failed to delete proforma invoice'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const filtered = (() => {
    let r = items.filter(i => (i.ref_number || '').toLowerCase().includes(searchQuery.toLowerCase()) || (i.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()));
    if (applied.customerId) r = r.filter(i => i.customer_id === applied.customerId);
    if (applied.company) r = r.filter(i => i.company_name === applied.company);
    if (applied.status === 'converted') r = r.filter(i => i.converted_to_dc || i.converted_to_sale);
    if (applied.status === 'non_converted') r = r.filter(i => !i.converted_to_dc && !i.converted_to_sale);
    if (applied.dateFrom) r = r.filter(i => { const d = (i.invoice_date || i.created_at || '').split('T')[0]; return d >= applied.dateFrom; });
    if (applied.dateTo) r = r.filter(i => { const d = (i.invoice_date || i.created_at || '').split('T')[0]; return d <= applied.dateTo; });
    return r;
  })();

  return (
    <div data-testid="proforma-invoices-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Proforma Invoices</h1>
          <p className="text-slate-600 mt-1">Manage proforma invoices</p>
        </div>
        {perms.canAdd && (
        <button onClick={() => navigate('/admin/sale/proforma/new')} data-testid="create-proforma-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Create Proforma Invoice
        </button>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" /><input type="text" placeholder="Search by quotation number or customer..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input" className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" /></div>
      </div>
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3"><Filter className="h-4 w-4 text-slate-500" /><span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>{hasFilters && <button onClick={clearFilters} data-testid="clear-filters-btn" className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Customer</label><ProductAutocomplete value={fCustomerId} onChange={setFCustomerId} products={customers} testId="filter-customer" placeholder="Type customer..." /></div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label>
            <div className="relative">
              <input type="text" value={fCompanyInput} onChange={e => { setFCompanyInput(e.target.value); setFCompany(''); setShowCompanySuggest(true); }} onFocus={() => setShowCompanySuggest(true)} onBlur={() => setTimeout(() => setShowCompanySuggest(false), 150)} placeholder="Type company..." data-testid="filter-company-input" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
              {fCompanyInput && <button onClick={() => { setFCompany(''); setFCompanyInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>}
              {showCompanySuggest && companySuggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-auto">
                  {companySuggestions.map(name => (
                    <button key={name} onMouseDown={() => { setFCompany(name); setFCompanyInput(name); setShowCompanySuggest(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${fCompany === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>{name}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Status</label><select value={fStatus} onChange={e => setFStatus(e.target.value)} data-testid="filter-status" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white"><option value="">All</option><option value="converted">Converted</option><option value="non_converted">Non-Converted</option></select></div>
          <div><button onClick={applyFilters} data-testid="apply-filters-btn" className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">Apply</button></div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Date</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {[{ key: 'this_month', label: 'This Month' }, { key: 'last_month', label: 'Last Month' }, { key: 'this_quarter', label: 'This Quarter' }, { key: 'last_quarter', label: 'Last Quarter' }, { key: 'this_year', label: 'This Year' }, { key: 'last_year', label: 'Last Year' }].map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`} className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${fActivePreset === p.key ? 'bg-orange-500 text-white border-orange-500' : 'border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'}`}>{p.label}</button>
              ))}
            </div>
            <div className="h-5 w-px bg-slate-200 hidden md:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label><input type="date" value={fDateFrom} onChange={e => { setFDateFrom(e.target.value); setFActivePreset(''); }} data-testid="date-from" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium">To</label><input type="date" value={fDateTo} onChange={e => { setFDateTo(e.target.value); setFActivePreset(''); }} data-testid="date-to" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No proforma invoices yet</h3>
            <p className="text-slate-500 mb-6">Create your first proforma invoice</p>
            <button onClick={() => navigate('/admin/sale/proforma/new')}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <Plus className="h-5 w-5" /> Create First Proforma
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-8 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Quotation No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Customer</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Total</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((item) => {
              const convertedDc = item.converted_to_dc && item.delivery_challan_id;
              const convertedSale = item.converted_to_sale && item.sale_invoice_id;
              return (
              <div key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                <div data-testid={`proforma-row-${item.id}`}
                  className="grid grid-cols-1 md:grid-cols-8 gap-4 px-6 pt-4 items-center">
                  <div className="text-slate-600 text-sm">{formatDate(item.invoice_date || item.created_at)}</div>
                  <div className="col-span-2 font-mono font-medium text-slate-900">{item.ref_number || '-'}</div>
                  <div className="text-slate-700 text-sm">{item.customer_name || '-'}</div>
                  <div className="text-slate-700 text-sm">{item.company_name || '-'}</div>
                  <div className="font-medium text-slate-900">₹{(item.total || 0).toLocaleString()}</div>
                  <div className="text-sm flex flex-wrap gap-1">
                    {convertedDc && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/sale/delivery-challan/${item.delivery_challan_id}`); }}
                        data-testid={`converted-dc-link-${item.id}`}
                        className="inline-flex items-center px-2 py-1 text-[10px] font-bold uppercase rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                      >
                        Challan
                      </button>
                    )}
                    {convertedSale && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/sale/invoices/${item.sale_invoice_id}`); }}
                        data-testid={`converted-sale-link-${item.id}`}
                        className="inline-flex items-center px-2 py-1 text-[10px] font-bold uppercase rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                      >
                        Sale
                      </button>
                    )}
                    {!convertedDc && !convertedSale && (
                      <span className="inline-flex items-center px-2 py-1 text-[10px] font-bold uppercase rounded bg-slate-100 text-slate-600" data-testid={`open-badge-${item.id}`}>Open</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/admin/sale/proforma/${item.id}`)} data-testid={`view-btn-${item.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                      <Eye className="h-4 w-4" />
                    </button>
                    {perms.canDelete && (
                    <button onClick={() => setDeleteConfirm(item.id)} data-testid={`delete-btn-${item.id}`}
                      className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this proforma invoice?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
